import { format } from "date-fns"
import { useEffect, useState } from "react"
import { Helmet } from "react-helmet"
import { useNavigate, useParams } from "react-router-dom"
import { toast, ToastContainer } from "react-toastify"
import logoHeader from "../../assets/logoHeader.png"
import Footer from "../../components/footer/Footer"
import { FloatingHelpButton, HelpPopup, useHelp } from "../../components/help"
import { BatchTrackingResponse } from "../../interface/TrackingInterface"
import TrackingService, {
    TRACKING_ERROR_CODES,
    TrackingErrorResponse
} from "../../services/TrackinService"

const TOAST_ERROR_STYLE = {
    backgroundColor: "#f44336",
    color: "#fff",
    padding: "16px",
    borderRadius: "10px",
    border: "2px solid #D32F2F",
    fontSize: "16px"
}

const ERROR_MESSAGES = {
    INVALID_PARAMS: "Parâmetros inválidos na URL",
    NOT_FOUND:
        "Não foi possível encontrar informações para os dados informados",
    GENERIC_ERROR: "Erro ao obter dados",
    UNAUTHORIZED: "Token de acesso inválido ou expirado"
}

const ERROR_CODE_MESSAGES = {
    [TRACKING_ERROR_CODES.CONNECTION_REFUSED_EXECUTING_POST]:
        "Conexão recusada ao executar requisição POST",
    [TRACKING_ERROR_CODES.CONNECTION_REFUSED]: "Conexão recusada pelo servidor",
    [TRACKING_ERROR_CODES.NETWORK_ERROR]: "Erro de conexão de rede",
    [TRACKING_ERROR_CODES.TIMEOUT_ERROR]: "Tempo limite de conexão excedido",
    [TRACKING_ERROR_CODES.API_ERROR]: "Erro na API de rastreamento",
    [TRACKING_ERROR_CODES.INVALID_RESPONSE]: "Resposta inválida do servidor",
    [TRACKING_ERROR_CODES.NO_INVOICE_FOUND]: "Nenhuma nota fiscal encontrada",
    [TRACKING_ERROR_CODES.NOT_FOUND]: "Dados não encontrados",
    [TRACKING_ERROR_CODES.INVALID_DATA]: "Dados inválidos",
    [TRACKING_ERROR_CODES.UNKNOWN_ERROR]: "Erro desconhecido"
}

export default function TrackingDirectScreen() {
    const { numerodanota, serie, token } = useParams<{
        numerodanota: string
        serie: string
        token: string
    }>()

    const [showDetails, setShowDetails] = useState(false)
    const [trackingInformation, setTrackingInformation] =
        useState<BatchTrackingResponse>()
    const [isLoading, setIsLoading] = useState(true)
    const [hasError, setHasError] = useState(false)
    const [savedToken, setSavedToken] = useState<string | undefined>()

    const navigate = useNavigate()

    const {
        showHelpPopup,
        handleShowHelpPopup,
        handleCloseHelpPopup,
        handleWhatsAppRedirect
    } = useHelp()

    const getStorageKey = (nota: string, serieNota: string) => {
        return `tracking_token_${nota}_${serieNota}`
    }

    useEffect(() => {
        if (numerodanota && serie) {
            const storageKey = getStorageKey(numerodanota, serie)

            if (token) {
                sessionStorage.setItem(storageKey, token)
                setSavedToken(token)
                navigate(`/rastreamento/${numerodanota}/${serie}`, {
                    replace: true
                })
            } else {
                const storedToken = sessionStorage.getItem(storageKey)
                if (storedToken) {
                    setSavedToken(storedToken)
                }
            }
        }
    }, [])

    useEffect(() => {
        return () => {
            if (numerodanota && serie) {
                const storageKey = getStorageKey(numerodanota, serie)
                sessionStorage.removeItem(storageKey)
            }
        }
    }, [])

    const showErrorToast = (message: string, errorCode?: string) => {
        const fullMessage = errorCode
            ? `${message} [Código: ${errorCode}]`
            : message
        toast.error(fullMessage, { style: TOAST_ERROR_STYLE })
        setHasError(true)
    }

    const formatDate = (
        dateString: string | null,
        includeTime = false
    ): string => {
        if (!dateString) return "N/A"

        try {
            const date = dateString.includes("T")
                ? new Date(dateString)
                : new Date(dateString.replace(" ", "T"))

            return format(
                date,
                includeTime ? "dd/MM/yyyy HH:mm:ss" : "dd/MM/yyyy"
            )
        } catch {
            return "N/A"
        }
    }

    const formatOccurrenceDescription = (
        description: string
    ): { main: string; details: string } => {
        if (!description.includes("-")) {
            return { main: description, details: "" }
        }

        const parts = description.split("-")
        const main = parts[0]
        const details = parts.slice(1).join(" ").trim()

        return { main, details }
    }

    const fetchTrackingData = async () => {
        if (!numerodanota || !serie || !savedToken) {
            showErrorToast(ERROR_MESSAGES.INVALID_PARAMS)
            setIsLoading(false)
            return
        }

        setIsLoading(true)
        setHasError(false)

        try {
            const response = await TrackingService.getBatchAllOccurrencesUnit(
                [{ numero: numerodanota, serie: serie }],
                savedToken
            )

            if (response && response.length > 0) {
                const todasOcorrencias: any[] = []

                response.forEach((item) => {
                    if (item.ocorrencias && Array.isArray(item.ocorrencias)) {
                        todasOcorrencias.push(...item.ocorrencias)
                    }
                })

                const trackingData = {
                    ...response[0],
                    ocorrencias: todasOcorrencias
                }

                setTrackingInformation(trackingData)
                setShowDetails(true)
            } else {
                showErrorToast(
                    ERROR_MESSAGES.NOT_FOUND,
                    TRACKING_ERROR_CODES.NO_INVOICE_FOUND
                )
            }
        } catch (error: any) {
            if (error.code && error.message) {
                const customError = error as TrackingErrorResponse
                const userMessage =
                    ERROR_CODE_MESSAGES[customError.code] ||
                    ERROR_MESSAGES.GENERIC_ERROR
                showErrorToast(userMessage, customError.code)
            } else if (
                error.response?.status === 401 ||
                error.response?.status === 403
            ) {
                showErrorToast(ERROR_MESSAGES.UNAUTHORIZED, "AUTH001")
            } else if (error.message) {
                showErrorToast(error.message, TRACKING_ERROR_CODES.API_ERROR)
            } else {
                showErrorToast(
                    ERROR_MESSAGES.NOT_FOUND,
                    TRACKING_ERROR_CODES.UNKNOWN_ERROR
                )
            }
        } finally {
            setIsLoading(false)
        }
    }

    const handleClick = (value: string) => {
        navigate("/" + value)
    }

    useEffect(() => {
        document.title = "Logplace - Rastreamento"
        if (savedToken) {
            fetchTrackingData()
        }
    }, [numerodanota, serie, savedToken])

    const getContainerClasses = () => {
        return "px-4 xl:px-44 min-h-[40rem] lg:h-[45rem] py-8 flex flex-row justify-center xl:justify-center w-full bg-gradient-to-b from-white from-1% via-secondary-1 via-30% via-secondary-2 via-30% to-primary to-80% z-0 items-center"
    }

    const getDetailsClasses = () => {
        const baseClasses =
            "w-full max-w-full h-auto lg:h-[38rem] rounded-lg bg-white transition-opacity duration-1000 p-4 flex flex-col gap-4 overflow-hidden"
        const visibilityClasses = showDetails
            ? "opacity-100"
            : "opacity-0 pointer-events-none hidden"

        return `${baseClasses} ${visibilityClasses}`
    }

    const NotaFiscalInfo = () => {
        if (!trackingInformation) return null

        const { notafiscal, ocorrencias } = trackingInformation

        return (
            <div className="p-4 rounded-lg bg-slate-200 flex-shrink-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Informações da Nota Fiscal */}
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                        <h3 className="text-lg font-bold text-gray-800 mb-3 border-b pb-2">
                            📋 Nota Fiscal
                        </h3>
                        <div className="space-y-2 text-sm">
                            <p className="flex justify-between">
                                <span className="font-semibold text-gray-600">
                                    Número:
                                </span>
                                <span className="text-gray-800">
                                    {notafiscal.numero}
                                </span>
                            </p>
                            <p className="flex justify-between">
                                <span className="font-semibold text-gray-600">
                                    Série:
                                </span>
                                <span className="text-gray-800">
                                    {notafiscal.serie}
                                </span>
                            </p>
                            <p className="flex justify-between">
                                <span className="font-semibold text-gray-600">
                                    Emissão:
                                </span>
                                <span className="text-gray-800">
                                    {formatDate(notafiscal.emissao || null)}
                                </span>
                            </p>
                            <p className="flex justify-between">
                                <span className="font-semibold text-gray-600">
                                    Data Limite:
                                </span>
                                <span className="text-gray-800">
                                    {formatDate(notafiscal.datalimite || null)}
                                </span>
                            </p>
                        </div>
                    </div>

                    {/* Informações do Destinatário */}
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                        <h3 className="text-lg font-bold text-gray-800 mb-3 border-b pb-2">
                            👤 Destinatário
                        </h3>
                        <div className="space-y-2 text-sm">
                            <p>
                                <span className="font-semibold text-gray-600">
                                    Nome:
                                </span>
                                <br />
                                <span className="text-gray-800">
                                    {notafiscal.destinatario}
                                </span>
                            </p>
                            <p className="flex justify-between">
                                <span className="font-semibold text-gray-600">
                                    CPF/CNPJ:
                                </span>
                                <span className="text-gray-800">
                                    {notafiscal.cpfCnpjDestinatario}
                                </span>
                            </p>
                            <p>
                                <span className="font-semibold text-gray-600">
                                    Fornecedor:
                                </span>
                                <br />
                                <span className="text-gray-800">
                                    {notafiscal.cnpjFornecedor || "N/A"}
                                </span>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const OccurrencesTable = () => {
        if (!trackingInformation?.ocorrencias) return null

        return (
            <div className="p-4 overflow-y-auto rounded-lg flex-1 bg-slate-200 min-h-0">
                <table className="w-full">
                    <thead>
                        <tr className="flex flex-row flex-1">
                            <th className="text-center flex-3">Descrição</th>
                            <th className="flex-1 text-center">Status</th>
                            <th className="flex-1 text-center">Data</th>
                        </tr>
                    </thead>
                    <tbody>
                        {trackingInformation.ocorrencias.map(
                            (ocorrencia, index) => {
                                const { main, details } =
                                    formatOccurrenceDescription(
                                        ocorrencia.descricaoKronos ||
                                            ocorrencia.descricao
                                    )

                                return (
                                    <tr
                                        className="flex flex-1 border-b-2 border-gray-600"
                                        key={`${ocorrencia.numOcorrencia}-${Math.random()}`}
                                    >
                                        <td className="p-2 text-sm flex-3">
                                            {main}
                                            {details && (
                                                <>
                                                    <br />
                                                    {details}
                                                </>
                                            )}
                                        </td>
                                        <td className="flex-1 p-2 font-semibold text-center">
                                            {ocorrencia.status}
                                        </td>
                                        <td className="flex-1 text-sm text-center">
                                            {formatDate(
                                                ocorrencia.momento,
                                                true
                                            )}
                                        </td>
                                    </tr>
                                )
                            }
                        )}
                    </tbody>
                </table>
            </div>
        )
    }

    const LoadingState = () => (
        <div className="w-full max-w-2xl p-8 bg-white rounded-lg shadow-lg">
            <div className="flex flex-col items-center justify-center space-y-4">
                <div className="w-16 h-16 border-4 border-secondary-1 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-xl font-semibold text-gray-700">
                    Buscando informações de rastreamento...
                </p>
                <p className="text-sm text-gray-500">
                    Aguarde enquanto consultamos os dados da sua encomenda
                </p>
            </div>
        </div>
    )

    const ErrorState = () => (
        <div className="w-full max-w-2xl p-8 bg-white rounded-lg shadow-lg">
            <div className="flex flex-col items-center justify-center space-y-4">
                <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full">
                    <svg
                        className="w-8 h-8 text-red-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </div>
                <p className="text-xl font-semibold text-gray-700">
                    Não foi possível encontrar as informações
                </p>
                <p className="text-sm text-center text-gray-500">
                    Verifique se o link está correto ou entre em contato com o
                    suporte
                </p>
            </div>
        </div>
    )

    return (
        <>
            <Helmet>
                <title>LogPlace - Rastreamento</title>
                <meta
                    name="description"
                    content="Oferecemos soluções logísticas sob medida para sua operação. Fulfillment, B2B, B2C com inteligência e tecnologia."
                />
                <meta
                    name="keywords"
                    content="logística, fulfillment, B2C, B2B, transporte, soluções logísticas, entregas, e-commerce"
                />
                <meta name="author" content="LogPlace" />
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1.0"
                />
                <meta property="og:title" content="Rastreamento" />
                <meta
                    property="og:description"
                    content="Somos líderes em soluções logísticas para o pequeno, médio e-commerce e grandes operações de varejo."
                />
                <meta property="og:url" content="https://logplace.com.br" />
                <meta property="og:type" content="website" />
            </Helmet>

            <div className="relative flex flex-col flex-1 w-full h-screen overflow-y-auto bg-white">
                <ToastContainer
                    position="top-right"
                    autoClose={1500}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    icon={false}
                    limit={1}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                />

                {/* Header */}
                <div className="flex flex-col items-center justify-center w-full py-6 space-y-4">
                    {/* Logo */}
                    <button
                        onClick={() => handleClick("home")}
                        className="flex items-center justify-center h-24 bg-white rounded-lg w-72 shadow-sm"
                    >
                        <img
                            className="w-[100%] h-[90%]"
                            src={logoHeader}
                            alt="LogPlace Logo"
                        />
                    </button>
                </div>

                {/* Main Content */}
                <div className={getContainerClasses()}>
                    {isLoading && <LoadingState />}

                    {!isLoading && hasError && <ErrorState />}

                    {!isLoading && !hasError && showDetails && (
                        <div className={getDetailsClasses()}>
                            {trackingInformation && (
                                <>
                                    <NotaFiscalInfo />
                                    <OccurrencesTable />
                                </>
                            )}
                        </div>
                    )}
                </div>

                <Footer />

                <FloatingHelpButton
                    isVisible={true}
                    onClick={handleShowHelpPopup}
                />

                <HelpPopup
                    isOpen={showHelpPopup}
                    onClose={handleCloseHelpPopup}
                    onWhatsApp={handleWhatsAppRedirect}
                    isTrackingPage={true}
                />
            </div>
        </>
    )
}
